
import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BookOpen, Video, FileCheck, UserCheck, Brain, Clock } from 'lucide-react';

const InterviewTips = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-8 mt-28 md:mt-32">
        <h1 className="text-3xl font-bold mb-6">Application & Interview Tips</h1>
        <div className="prose max-w-none">
          <p className="mb-6 text-lg">
            Applying for a position through our platform involves multiple steps. Here are specific tips to help you succeed at each stage of our application process.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-center mb-4">
                <FileCheck className="text-blue-600 mr-3" size={24} />
                <h2 className="text-xl font-semibold">Resume Preparation</h2>
              </div>
              <ul className="list-disc pl-5 space-y-2">
                <li>Tailor your resume to highlight skills relevant to the specific job posting</li>
                <li>Our AI analyzes your resume, so ensure key skills and experiences are clearly listed</li>
                <li>Use specific metrics and achievements rather than generic descriptions</li>
                <li>Include relevant certifications and technical skills that match the job requirements</li>
                <li>Keep your employment history concise but comprehensive</li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-center mb-4">
                <Brain className="text-purple-600 mr-3" size={24} />
                <h2 className="text-xl font-semibold">Technical Assessment</h2>
              </div>
              <ul className="list-disc pl-5 space-y-2">
                <li>Our assessment includes both multiple-choice and descriptive questions</li>
                <li>Review core concepts related to the position you're applying for</li>
                <li>Practice explaining technical solutions clearly and concisely</li>
                <li>Complete the assessment in one sitting - switching tabs may flag your application</li>
                <li>Be honest about your skills - our system detects AI-generated responses</li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-center mb-4">
                <Video className="text-red-600 mr-3" size={24} />
                <h2 className="text-xl font-semibold">Video Introduction</h2>
              </div>
              <ul className="list-disc pl-5 space-y-2">
                <li>Keep your video introduction under 5 minutes as required</li>
                <li>Choose a quiet, well-lit location with a neutral background</li>
                <li>Structure your introduction: brief personal intro, relevant experience, why you're interested</li>
                <li>Highlight 2-3 specific achievements relevant to the position</li>
                <li>Our AI analyzes communication skills, so speak clearly and maintain good eye contact</li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-center mb-4">
                <UserCheck className="text-green-600 mr-3" size={24} />
                <h2 className="text-xl font-semibold">Live Interview Preparation</h2>
              </div>
              <ul className="list-disc pl-5 space-y-2">
                <li>Research our company culture and values before your interview</li>
                <li>Be prepared to expand on projects mentioned in your video introduction</li>
                <li>Have specific examples ready that demonstrate your problem-solving skills</li>
                <li>Prepare thoughtful questions about the team, work culture, and expectations</li>
                <li>Test your video conferencing setup before the scheduled interview time</li>
              </ul>
            </div>
          </div>
          
          <div className="mt-10 bg-blue-50 rounded-lg p-6 border border-blue-100">
            <div className="flex items-start">
              <Clock className="text-blue-600 mt-1 mr-3 flex-shrink-0" size={24} />
              <div>
                <h3 className="text-xl font-semibold mb-2">Application Timeline</h3>
                <p className="mb-4">Our typical application process follows these steps:</p>
                <ol className="list-decimal pl-5 space-y-2">
                  <li>Initial application submission with resume</li>
                  <li>Technical skills assessment (typically 30-45 minutes)</li>
                  <li>Video introduction recording (maximum 5 minutes)</li>
                  <li>Application review by our hiring team (1-2 weeks)</li>
                  <li>Live interview with team members (if selected)</li>
                  <li>Final decision and offer (within 1 week of final interview)</li>
                </ol>
              </div>
            </div>
          </div>
          
          <div className="mt-10 bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <BookOpen className="text-amber-600 mr-3" size={24} />
              <h2 className="text-xl font-semibold">Additional Resources</h2>
            </div>
            <p className="mb-4">
              To help you prepare for our application process, we recommend reviewing these resources:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Check our blog for articles on industry trends and technical skills we value</li>
              <li>Review our FAQ section for common questions about our hiring process</li>
              <li>Visit our careers page to learn more about our company culture and values</li>
              <li>Connect with us on social media to stay updated on company news and events</li>
            </ul>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default InterviewTips;
